import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';

import { SharedContainer } from '../../components/shared-container/container';
import { SharedCard } from '../../components/shared-card/card';
import optimoLogo from '../../logo/OptimoCapitalLogoWB.jpg';
import OverlayLoader from '../../components/overlayLoader';
import Navbar from '../../components/shared-menu/Navbar';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Error } from '@mui/icons-material';
import { useAuth } from '../../services/auth/AuthContext';
import DigioService from '../../services/api/digio';
import { DIGIO_ENVIRONMENT } from '../../config';

const getDigioOptions = () => ({
  environment: DIGIO_ENVIRONMENT,
  logo: "https://partner.optimocapital.in/static/media/OptimoCapitalLogoWY.339c56e306453ec4d0fe46896513b88a.svg",
  is_redirection_approach: true,
  redirect_url: window.location.href,
  theme: {
    primaryColor: "#1F002A",
    secondaryColor: "#444444",
  },
});

export default function KYCRegistration() {
  const { t } = useTranslation();

  const { setIsAuthenticated, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const kycId = searchParams.get('digio_doc_id');

  const [digio, setDigio] = useState(null);
  const [kycData, setKycData] = useState(null);
  const [loading, setLoading] = useState(false);

  const aadhaarVerified = kycData?.aadhaar;
  const panVerified = kycData?.pan;

  const digioSign = async () => {
    const kycRequest = await DigioService.createKycRequest(setIsAuthenticated, navigate);
    const digioAccessToken = kycRequest.access_token;
    digio.init();
    digio.submit(
      digioAccessToken.entity_id,
      kycRequest.customer_identifier,
      digioAccessToken.id,
    );
  }

  useEffect(() => {
    if (status !== 'success' || !kycId) {
      return;
    }

    setLoading(true);
    DigioService.verifyKycRequest(kycId, setIsAuthenticated, navigate)
      .then((res) => {
        setKycData(res.data);
        if (res.data.aadhaar && res.data.pan) {
          refreshUserData();
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [kycId, navigate, refreshUserData, setIsAuthenticated, status]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://app.digio.in/sdk/v11/digio.js";
    script.onload = () => {
      const digio = new window.Digio(getDigioOptions());
      setDigio(digio);
    }
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    }
  }, []);

  if (!digio || loading) {
    return (
      <OverlayLoader
        open
        text=""
      />
    );
  }

  return (

      <SharedContainer className='custom-class' direction="column" justifyContent="space-between">
        <Navbar isTransparent />
        <SharedCard>
          <img src={optimoLogo} alt="Optimo Logo" style={{ width: '80px', objectFit: 'contain' }} />
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h4" component="h1">
              {t('kyc.header')}
            </Typography>
          </Box>

          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {aadhaarVerified ? (
                <>
                  <DoneAllIcon color="success" />
                  <Typography variant="body1" color="textSecondary">
                    {t('kyc.aadhaar_verified')}
                  </Typography>
                </>
              ) : (
                <>
                  <Error color="error" />
                  <Typography variant="body1" color="textSecondary">
                    {t('kyc.aadhaar_not_verified')}
                  </Typography>
                </>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {panVerified ? (
                <>
                  <DoneAllIcon color="success" />
                  <Typography variant="body1" color="textSecondary">
                    {t('kyc.pan_verified')}
                  </Typography>
                </>
              ) : (
                <>
                  <Error color="error" />
                  <Typography variant="body1" color="textSecondary">
                    {t('kyc.pan_not_verified')}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {(!aadhaarVerified || !panVerified) && (
            <Button
              variant="contained"
              color="primary"
              onClick={digioSign}
            >
              {t('kyc.complete_kyc')}
            </Button>
          )}
        </SharedCard>
      </SharedContainer>
  );
}
